import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';

// ── Type Definitions ──────────────────────────────────────────

interface Entrance {
  id: string;
  eventId: string;
  name: string;
  entryCount: number;
}

interface ApiError {
  statusCode?: number;
  message?: string;
}

interface EntranceManagementProps {
  eventId: string;
  hasActiveSession: boolean;
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1.5rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.2rem',
    fontWeight: 700,
    color: TEXT_DARK,
    marginTop: 0,
    marginBottom: '1rem',
    borderBottom: `2px solid ${PRIMARY}`,
    paddingBottom: '0.25rem',
  },
  lockBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #f59e0b',
    borderRadius: '6px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    color: '#92400e',
    fontWeight: 500,
  },
  addForm: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    marginBottom: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    flex: 1,
    maxWidth: '280px',
  },
  addBtn: {
    padding: '0.4rem 0.9rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  addBtnDisabled: {
    padding: '0.4rem 0.9rem',
    backgroundColor: '#9ca3af',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'not-allowed',
    whiteSpace: 'nowrap' as const,
  },
  entranceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  entranceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1,
  },
  entranceName: {
    fontSize: '0.9rem',
    color: TEXT_DARK,
    fontWeight: 500,
  },
  countBadge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: '0.25rem',
    alignItems: 'center',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  },
  iconBtnDisabled: {
    background: 'none',
    border: 'none',
    cursor: 'not-allowed',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    opacity: 0.4,
  },
  editInput: {
    padding: '0.3rem 0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    width: '180px',
  },
  saveBtn: {
    padding: '0.25rem 0.5rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.8rem',
    marginBottom: '0.75rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
  },
  emptyState: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    textAlign: 'center' as const,
    padding: '2rem 1rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function EntranceManagement({ eventId, hasActiveSession }: EntranceManagementProps) {
  const [entrances, setEntrances] = useState<Entrance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form state
  const [newName, setNewName] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // ── Data Fetching ─────────────────────────────────────────────

  async function fetchEntrances() {
    try {
      const data = await apiClient<Entrance[]>(`/gate/events/${eventId}/entrances`);
      setEntrances(data);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to load entrances.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEntrances();
  }, [eventId]);

  // ── Actions ───────────────────────────────────────────────────

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;

    try {
      await apiClient(`/gate/events/${eventId}/entrances`, {
        method: 'POST',
        body: { name: trimmed },
      });
      setNewName('');
      setError('');
      fetchEntrances();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        setError(apiErr.message || 'An entrance with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to create entrance.');
      }
    }
  }

  function startEdit(entrance: Entrance) {
    setEditingId(entrance.id);
    setEditName(entrance.name);
    setError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      setError('Entrance name is required.');
      return;
    }

    try {
      await apiClient(`/gate/entrances/${editingId}`, {
        method: 'PATCH',
        body: { name: trimmed },
      });
      cancelEdit();
      setError('');
      fetchEntrances();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        setError(apiErr.message || 'An entrance with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to update entrance.');
      }
    }
  }

  async function handleDelete(entrance: Entrance) {
    try {
      await apiClient(`/gate/entrances/${entrance.id}`, { method: 'DELETE' });
      setError('');
      fetchEntrances();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        setError(apiErr.message || 'Cannot delete entrance with recorded entries.');
      } else {
        setError(apiErr.message || 'Failed to delete entrance.');
      }
    }
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Manage Guest Entrance/Exit</h2>

      {/* Lock Banner */}
      {hasActiveSession && (
        <div style={styles.lockBanner} role="alert">
          🔒 Entrance modifications are locked during an active session
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={styles.error} role="alert">
          {error}
        </div>
      )}

      {/* Add Entrance Form */}
      <form onSubmit={handleAdd} style={styles.addForm}>
        <input
          style={styles.input}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Entrance name"
          maxLength={100}
          disabled={hasActiveSession}
          aria-label="New entrance name"
        />
        <button
          type="submit"
          style={hasActiveSession ? styles.addBtnDisabled : styles.addBtn}
          disabled={hasActiveSession || !newName.trim()}
          aria-label="Add entrance"
        >
          Add Entrance
        </button>
      </form>

      {/* Entrance List */}
      {loading ? (
        <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading entrances...</p>
      ) : entrances.length === 0 ? (
        <p style={styles.emptyState}>No entrances defined yet. Add one above to get started.</p>
      ) : (
        <div>
          {entrances.map((entrance) =>
            editingId === entrance.id ? (
              <div key={entrance.id} style={styles.entranceRow}>
                <div style={styles.entranceInfo}>
                  <input
                    style={styles.editInput}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={100}
                    autoFocus
                    aria-label={`Edit name for ${entrance.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                </div>
                <div style={styles.actions}>
                  <button
                    style={styles.saveBtn}
                    onClick={handleSaveEdit}
                    aria-label="Save changes"
                  >
                    Save
                  </button>
                  <button
                    style={styles.cancelBtn}
                    onClick={cancelEdit}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={entrance.id} style={styles.entranceRow}>
                <div style={styles.entranceInfo}>
                  <span style={styles.entranceName}>{entrance.name}</span>
                  <span style={styles.countBadge}>
                    {entrance.entryCount} {entrance.entryCount === 1 ? 'entry' : 'entries'}
                  </span>
                </div>
                <div style={styles.actions}>
                  <button
                    style={hasActiveSession ? styles.iconBtnDisabled : styles.iconBtn}
                    onClick={() => startEdit(entrance)}
                    disabled={hasActiveSession}
                    title="Edit"
                    aria-label={`Edit ${entrance.name}`}
                  >
                    ✏️
                  </button>
                  <button
                    style={hasActiveSession ? styles.iconBtnDisabled : styles.iconBtn}
                    onClick={() => handleDelete(entrance)}
                    disabled={hasActiveSession}
                    title="Delete"
                    aria-label={`Delete ${entrance.name}`}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
